import { useEffect, useRef } from 'preact/hooks'
import type { Flow } from '../flow'
import type { RoomInfo, Session } from '../session'
import styles from '../ui.module.css'
import { RoomCloseCountdown } from './RoomCloseCountdown'

interface Props {
  session: Session
  flow: Flow
  room: RoomInfo | null
}

/**
 * The results' buttons (moved out of `Results.tsx` for PHASE3 T3.5, same rules, D-035).
 * Public: Race again (a new public lobby). Private: the host's Rematch (same players, back to
 * the lobby); everyone else waits for it. Hub leaves. After the server closes the room only
 * Race again and Hub remain; until then a private room counts down to its closing (D-066).
 * Keyboard players land on the first button.
 */
export function ResultActions({ session, flow, room }: Props) {
  const first = useRef<HTMLButtonElement>(null)
  const isPrivate = room !== null && room.code !== ''
  const canRematch = isPrivate && room.isHost && !flow.closed
  const canRaceAgain = !isPrivate || flow.closed
  const holdMs = isPrivate && !flow.closed ? (session.resultsHoldMs ?? null) : null

  useEffect(() => {
    // Keyboard players land on the first action (Rematch, Race again or Hub).
    first.current?.focus()
  }, [canRematch, canRaceAgain])

  return (
    <div data-testid="result-actions">
      {isPrivate && !canRematch && (
        <p class={styles.status} aria-live="polite">
          {flow.closed ? 'The room has closed.' : 'Waiting for the host…'}
        </p>
      )}
      {holdMs !== null && <RoomCloseCountdown holdMs={holdMs} />}
      <div class={styles.row}>
        {canRematch && (
          <button
            ref={first}
            type="button"
            class={`${styles.button} ${styles.primary}`}
            onClick={() => session.rematch()}
          >
            Rematch
          </button>
        )}
        {canRaceAgain && (
          <button
            ref={first}
            type="button"
            class={`${styles.button} ${styles.primary}`}
            onClick={() => session.playPublic()}
          >
            Race again
          </button>
        )}
        <button
          ref={canRematch || canRaceAgain ? null : first}
          type="button"
          class={styles.button}
          onClick={() => session.toMenu()}
        >
          Hub
        </button>
      </div>
    </div>
  )
}
